import React, { Component } from 'react';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatListingPriceForDisplay } from '../../../util/displayCurrency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';
import { DisplayCurrencyContext } from '../../../context/displayCurrencyContext';

import css from './SearchMapPriceLabel.module.css';

/**
 * SearchMapPriceLabel component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.listing} props.listing - The listing
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {Object} props.config - The configuration
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class SearchMapPriceLabel extends Component {
  static contextType = DisplayCurrencyContext;

  shouldComponentUpdate(nextProps, _nextState, nextContext) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    const hasSameDisplayCurrency =
      this.context?.displayCurrency === nextContext?.displayCurrency &&
      this.context?.eurToUsdRate === nextContext?.eurToUsdRate;

    return !(
      isSameListing &&
      hasSamePrice &&
      hasSameActiveStatus &&
      hasSameRefreshToken &&
      hasSameDisplayCurrency
    );
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
    } = this.props;
    const { displayCurrency, eurToUsdRate } = this.context || {};
    const currentListing = ensureListing(listing);
    const { price, publicData, title } = currentListing.attributes;

    const { label: formattedPrice } = formatListingPriceForDisplay({
      intl,
      price,
      publicData,
      displayCurrency: displayCurrency || config.currency,
      marketplaceCurrency: config.currency,
      eurToUsdRate,
    });

    const priceValue = formattedPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.price' }, { priceValue: formattedPrice })
      : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = hasMultiplePriceVariants
      ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue })
      : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue });

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    const ariaLabel = priceValue
      ? priceMessage
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() => onListingClicked(currentListing)}
        aria-label={ariaLabel}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{priceMessage}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

export default injectIntl(SearchMapPriceLabel);
